// src/components/RiderTracking.js
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Box,
  Grid,
  Paper,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  Divider,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
} from '@react-google-maps/api';

// Styles for the sidebar
const Sidebar = styled(Paper)(({ theme }) => ({
  height: '100%',
  overflowY: 'auto',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

const RiderAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
}));

// Google Maps container style
const mapContainerStyle = {
  width: '100%',
  height: '60vh',
};

// Default center (Sri Lanka) until a rider is selected
const defaultCenter = {
  lat: 7.8731,
  lng: 80.7718,
};

// Load only the "places" library
const libraries = ['places'];

const RiderTracking = () => {
  const theme = useTheme();

  const [riders, setRiders] = useState([]);
  const [selectedRider, setSelectedRider] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [loading, setLoading] = useState(true);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);

  // 1) Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB2-q_0T2LB2sA1X_syazc4Y-yZ-IdmSvo',
    libraries,
  });

  // 2) Fetch rider locations from API
  useEffect(() => {
    const fetchRiderLocations = async () => {
      try {
        const response = await axios.get(
          'https://kirupam.shop/admin/admin_api/rider_tracking.php?action=fetch_locations'
        );
        const data = Array.isArray(response.data) ? response.data : [];
        setRiders(data);

        if (data.length > 0) {
          // Default to first rider
          setSelectedRider(data[0]);
          setMapCenter({
            lat: parseFloat(data[0].current_location_latitude),
            lng: parseFloat(data[0].current_location_longitude),
          });
        }
      } catch (error) {
        console.error('Error fetching rider locations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRiderLocations();
  }, []);

  // 3) Handler when a rider is clicked in the list
  const handleRiderClick = (rider) => {
    setSelectedRider(rider);
    setMapCenter({
      lat: parseFloat(rider.current_location_latitude),
      lng: parseFloat(rider.current_location_longitude),
    });
    setInfoWindowOpen(true);
  };

  // 4) Close InfoWindow when clicking away
  const handleInfoWindowClose = () => {
    setInfoWindowOpen(false);
  };

  // 5) Render loading spinner if data or maps not ready
  if (loading || !isLoaded) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="error">
          Error loading Google Maps. Please check your API key.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* ─────────── Sidebar with Rider List ─────────── */}
      <Box sx={{ width: { xs: '100%', sm: '30%' }, borderRight: `1px solid ${theme.palette.divider}` }}>
        <Sidebar elevation={0}>
          <Typography variant="h6" gutterBottom>
            Riders
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {riders.map((rider) => {
              const lastActive = rider.created_at
                ? moment(rider.created_at).format('MMM D, YYYY h:mm A')
                : 'Unknown';
              return (
                <ListItemButton
                  key={rider.id}
                  onClick={() => handleRiderClick(rider)}
                  selected={selectedRider && selectedRider.id === rider.id}
                  sx={{ mb: 1, borderRadius: 1 }}
                >
                  <ListItemAvatar>
                    <RiderAvatar>
                      {rider.name ? rider.name.charAt(0) : 'U'}
                    </RiderAvatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={rider.name || 'Unnamed Rider'}
                    secondary={`Last Active: ${lastActive}`}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Sidebar>
      </Box>

      {/* ─────────── Main Content ─────────── */}
      <Box sx={{ flexGrow: 1, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {/* ── Rider Location Card ── */}
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Rider Location
                </Typography>
                <Box sx={{ height: '60vh', width: '100%' }}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={15}
                  >
                    {selectedRider && (
                      <>
                        <Marker
                          position={{
                            lat: parseFloat(selectedRider.current_location_latitude),
                            lng: parseFloat(selectedRider.current_location_longitude),
                          }}
                          onClick={() => setInfoWindowOpen(true)}
                        />
                        {infoWindowOpen && (
                          <InfoWindow
                            position={{
                              lat: parseFloat(selectedRider.current_location_latitude),
                              lng: parseFloat(selectedRider.current_location_longitude),
                            }}
                            onCloseClick={handleInfoWindowClose}
                          >
                            <Box sx={{ maxWidth: 200 }}>
                              <Typography variant="subtitle1">
                                {selectedRider.name}
                              </Typography>
                              <Typography variant="body2">
                                Latitude: {selectedRider.current_location_latitude}
                              </Typography>
                              <Typography variant="body2">
                                Longitude: {selectedRider.current_location_longitude}
                              </Typography>
                              {selectedRider.created_at && (
                                <Typography variant="body2">
                                  Last Active:{' '}
                                  {moment(selectedRider.created_at).format('MMM D, YYYY h:mm A')}
                                </Typography>
                              )}
                            </Box>
                          </InfoWindow>
                        )}
                      </>
                    )}
                  </GoogleMap>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Rider Details Card ── */}
          <Grid item xs={12}>
            {selectedRider ? (
              <Card elevation={2}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Name:</Typography>
                      <Typography variant="body1">{selectedRider.name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Rider ID:</Typography>
                      <Typography variant="body1">{selectedRider.rider_id}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Last Active:</Typography>
                      <Typography variant="body1">
                        {selectedRider.created_at
                          ? moment(selectedRider.created_at).format('MMM D, YYYY h:mm A')
                          : 'Unknown'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Select a rider from the list to view details.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default RiderTracking;
